import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  Artist,
  ArtistCreateRequest,
  ArtistGroupCreateRequest,
  ArtistUpdateRequest,
} from '@/lib/api-admin/types';
import {
  ApiClientError,
  idempotencyKeyFor,
  TransportError,
  type ApiError as ApiErrorPayload,
  type ApiMeta,
} from '@/lib/http-core';
import { apiClient } from '@/lib/apiClient';
import { artistQueryKey } from './useArtistQuery';

interface ErrorEnvelopeShape {
  success: false;
  error: ApiErrorPayload;
  meta?: ApiMeta;
}

const throwIfError = (raw: unknown, status: number, op: string): never => {
  const env = raw as ErrorEnvelopeShape | null | undefined;
  if (env && env.success === false && env.error) {
    throw new ApiClientError(env.error, { status, ...(env.meta ? { meta: env.meta } : {}) });
  }
  throw new TransportError(`Artist ${op} failed with status ${status}`, raw);
};

const invalidateArtistCaches = async (
  qc: ReturnType<typeof useQueryClient>,
  artistId?: string,
) => {
  if (artistId) {
    await qc.invalidateQueries({ queryKey: artistQueryKey(artistId) });
  }
  await qc.invalidateQueries({ queryKey: ['admin', 'artists'] });
};

export const useCreateArtistMutation = () => {
  const qc = useQueryClient();
  return useMutation<Artist, Error, ArtistCreateRequest>({
    mutationFn: async (body) => {
      const path = '/admin/v1/artists' as const;
      const idempotencyKey = idempotencyKeyFor('POST', path, body);
      const { data, error, response } = await apiClient.raw.POST(path, {
        body,
        params: { header: { 'Idempotency-Key': idempotencyKey } },
      });
      if (!data || data.success !== true) throwIfError(error ?? data, response.status, 'create');
      return (data as { data: Artist }).data;
    },
    onSuccess: (artist) => invalidateArtistCaches(qc, artist.artistId),
  });
};

export const useCreateArtistGroupMutation = () => {
  const qc = useQueryClient();
  return useMutation<Artist, Error, ArtistGroupCreateRequest>({
    mutationFn: async (body) => {
      const path = '/admin/v1/artist-groups' as const;
      const idempotencyKey = idempotencyKeyFor('POST', path, body);
      const { data, error, response } = await apiClient.raw.POST(path, {
        body,
        params: { header: { 'Idempotency-Key': idempotencyKey } },
      });
      if (!data || data.success !== true)
        throwIfError(error ?? data, response.status, 'group-create');
      return (data as { data: Artist }).data;
    },
    onSuccess: (artist) => invalidateArtistCaches(qc, artist.artistId),
  });
};

export interface UpdateArtistInput {
  artistId: string;
  body: ArtistUpdateRequest;
}

export const useUpdateArtistMutation = () => {
  const qc = useQueryClient();
  return useMutation<Artist, Error, UpdateArtistInput>({
    mutationFn: async ({ artistId, body }) => {
      const path = '/admin/v1/artists/{artistId}' as const;
      const concretePath = path.replace('{artistId}', artistId);
      const idempotencyKey = idempotencyKeyFor('PATCH', concretePath, body);
      const { data, error, response } = await apiClient.raw.PATCH(path, {
        body,
        params: { path: { artistId }, header: { 'Idempotency-Key': idempotencyKey } },
      });
      if (!data || data.success !== true) throwIfError(error ?? data, response.status, 'update');
      return (data as { data: Artist }).data;
    },
    onSuccess: (_artist, { artistId }) => invalidateArtistCaches(qc, artistId),
  });
};

export const useArchiveArtistMutation = () => {
  const qc = useQueryClient();
  return useMutation<Artist, Error, string>({
    mutationFn: async (artistId) => {
      const path = '/admin/v1/artists/{artistId}' as const;
      const concretePath = path.replace('{artistId}', artistId);
      const idempotencyKey = idempotencyKeyFor('DELETE', concretePath, '');
      const { data, error, response } = await apiClient.raw.DELETE(path, {
        params: { path: { artistId }, header: { 'Idempotency-Key': idempotencyKey } },
      });
      if (!data || data.success !== true) throwIfError(error ?? data, response.status, 'archive');
      return (data as { data: Artist }).data;
    },
    onSuccess: (_artist, artistId) => invalidateArtistCaches(qc, artistId),
  });
};

export const usePurgeArtistMutation = () => {
  const qc = useQueryClient();
  return useMutation<Artist, Error, string>({
    mutationFn: async (artistId) => {
      const path = '/admin/v1/artists/{artistId}' as const;
      const concretePath = `${path.replace('{artistId}', artistId)}?purge=true`;
      const idempotencyKey = idempotencyKeyFor('DELETE', concretePath, '');
      const { data, error, response } = await apiClient.raw.DELETE(path, {
        params: {
          path: { artistId },
          query: { purge: true },
          header: { 'Idempotency-Key': idempotencyKey },
        } as { path: { artistId: string }; header: { 'Idempotency-Key': string } },
      });
      if (!data || data.success !== true) throwIfError(error ?? data, response.status, 'purge');
      return (data as { data: Artist }).data;
    },
    onSuccess: (_artist, artistId) => invalidateArtistCaches(qc, artistId),
  });
};
